import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  TextField,
} from "@mui/material";
import { Component } from "../../../../types/component";
import { Airframe } from "../../../../types/airframe";
import { SubAssembly } from "../../../../types/subAssembly";
import { getComponentTypeName } from "../../../../utils/componentTypeUtils";
import {
  ComponentSubAssemblyAttachInfo,
  isLandingGearCompatibleWithAirframe,
  isLandingGearComponent,
} from "../../../../utils/landingGearAttachUtils";
import { AttachedLandingGearSubAssemblies } from "../components/AttachedLandingGearSubAssemblies";

type Props = {
  open: boolean;
  airframe: Airframe | null;
  replaceTarget: Component | null;
  components: Component[];
  form: { componentId: string; slotId?: string };
  onChange: (f: { componentId: string; slotId?: string }) => void;
  onReplace: () => void;
  onClose: () => void;
  isReplacing?: boolean;
  subAssemblyByComponentId?: Record<string, ComponentSubAssemblyAttachInfo>;
  subAssemblies?: SubAssembly[];
};

const readonlyFieldSx = {
  "& .MuiInputLabel-root": { color: "#fff" },
  "& .MuiInputLabel-root.Mui-focused": { color: "hsl(19 101% 51% / 1)" },
  "& .MuiOutlinedInput-root": {
    color: "#fff",
    "& fieldset": { borderColor: "#fff" },
    "&:hover fieldset": { borderColor: "#fff" },
    "&.Mui-focused fieldset": {
      borderColor: "hsl(19 101% 51% / 1)",
    },
    "& input": {
      color: "#fff",
      WebkitTextFillColor: "#fff",
    },
  },
} as const;

export function ReplaceComponentDialog({
  open,
  airframe: afm,
  replaceTarget,
  components,
  form,
  onChange,
  onReplace,
  onClose,
  isReplacing = false,
  subAssemblyByComponentId = {},
  subAssemblies = [],
}: Props) {
  if (!open || !afm || !replaceTarget) return null;

  // Keep landing gear aligned to airframe family (CRJ/Embraer) or manufacturer.
  const filteredComponents = components.filter((c) =>
    isLandingGearCompatibleWithAirframe(c, afm.manufacturer)
  );

  const selectedSubAssemblyInfo = form.componentId
    ? subAssemblyByComponentId[form.componentId.trim().toLowerCase()]
    : undefined;

  const positionDisplay =
    selectedSubAssemblyInfo?.landingGearType?.trim() ||
    replaceTarget.attachmentLocation?.trim() ||
    "";

  const showReadonlyPosition = Boolean(positionDisplay);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: "#111",
          color: "#fff",
          border: "1px solid #333",
        },
      }}
    >
      <DialogTitle sx={{ color: "hsl(19 101% 51% / 1)" }}>
        Replace Assembly in {afm?.tailNumber}
      </DialogTitle>

      <DialogContent>
        <Typography sx={{ mb: 2, color: "#ccc" }}>
          Replacing: {replaceTarget.model} - {replaceTarget.serialNumber}
          {replaceTarget.attachmentLocation
            ? ` (${replaceTarget.attachmentLocation})`
            : ""}
        </Typography>

        {isLandingGearComponent(replaceTarget) && replaceTarget.id && (
          <AttachedLandingGearSubAssemblies
            componentId={replaceTarget.id}
            subAssemblies={subAssemblies}
          />
        )}

        <Grid container spacing={2} sx={{ mt: isLandingGearComponent(replaceTarget) ? 2 : 0 }}>
          <Grid size={{ xs: 12, sm: showReadonlyPosition ? 6 : 12 }}>
            <FormControl fullWidth required>
              <InputLabel required sx={{ color: "#fff" }}>Replacement</InputLabel>
              <Select
                value={
                  filteredComponents.some((c) => c.id === form.componentId)
                    ? form.componentId
                    : ""
                }
                onChange={(e) =>
                  onChange({
                    componentId: e.target.value as string,
                  })
                }
                sx={{
                  color: "#fff",
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#fff",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#fff",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "hsl(19 101% 51% / 1)",
                  },
                  "& .MuiSvgIcon-root": {
                    color: "#fff",
                  },
                }}
              >
                {filteredComponents.length > 0 ? (
                  filteredComponents.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {getComponentTypeName(c.componentType) || c.componentType} -{" "}
                      {c.manufacturer} {c.model} (S/N: {c.serialNumber})
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem disabled>No compatible replacement assemblies</MenuItem>
                )}
              </Select>
            </FormControl>
          </Grid>

          {showReadonlyPosition && (
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                required
                label="Position"
                value={positionDisplay}
                InputProps={{ readOnly: true }}
                InputLabelProps={{ shrink: true }}
                sx={readonlyFieldSx}
              />
            </Grid>
          )}
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} sx={{ color: "#fff" }} disabled={isReplacing}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={onReplace}
          disabled={isReplacing || !form.componentId}
          startIcon={isReplacing ? <CircularProgress size={20} sx={{ color: "#fff" }} /> : undefined}
          sx={{
            backgroundColor: "hsl(19 101% 51% / 1)",
            "&:hover": {
              backgroundColor: "hsl(19 101% 41% / 1)",
            },
          }}
        >
          {isReplacing ? "Replacing..." : "Replace"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
